import {
  CASE_TYPE_SCHEMA,
  normalizeEnumFields,
} from "../../../case-spec";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { JsonTree, parseJsonText } from "@/components/json-tree";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError } from "@/lib/api";
import { validateEditorPhoneJson } from "@/lib/phone-json-validate";
import { formatReadyGateIssueLine } from "@/lib/ready-gate-copy";
import { PhoneJsonEditorProvider } from "./editor-context";
import { JsonFormTree } from "./json-form-tree";
import { usePhoneJsonEditorSession } from "./phone-json-editor-session";
import { SectionNav } from "./section-nav";

export type PhoneJsonEditorMode = "form" | "tree" | "raw";

export function PhoneJsonEditorPanel() {
  const {
    setOpen,
    phoneJson: serverJson,
    caseId,
    versionId,
    canEdit,
    dirty,
    setDirty,
    saveBusy,
    saveError,
    onSave,
    placeholderCount,
    mediaReadyOk,
    readOnlyReason,
    phoneRuntimeVersion,
  } = usePhoneJsonEditorSession();

  const [mode, setMode] = useState<PhoneJsonEditorMode>("form");
  const [draft, setDraft] = useState<unknown>(serverJson);
  const [sectionPath, setSectionPath] = useState("game.puzzleSteps");
  const [rawText, setRawText] = useState("");
  const [rawError, setRawError] = useState<string | null>(null);
  const [validateIssues, setValidateIssues] = useState<
    Array<{ path: string; message: string }>
  >([]);
  const [mediaIssues, setMediaIssues] = useState<
    Array<{ path: string; reason: string }>
  >([]);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkNote, setCheckNote] = useState<string | null>(null);
  const [fixBusy, setFixBusy] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [fixNote, setFixNote] = useState<string | null>(null);
  const checkGenRef = useRef(0);

  const invalidateInFlightCheck = () => {
    checkGenRef.current += 1;
    setCheckBusy(false);
  };

  useEffect(() => {
    if (dirty) return;
    const next =
      serverJson == null
        ? serverJson
        : normalizeEnumFields(serverJson, CASE_TYPE_SCHEMA);
    setDraft(next);
    setRawText(next == null ? "" : JSON.stringify(next, null, 2));
    setRawError(null);
    setValidateIssues([]);
    setMediaIssues([]);
    setCheckNote(null);
    setFixError(null);
    setFixNote(null);
    checkGenRef.current += 1;
    setCheckBusy(false);
    if (next !== serverJson && serverJson != null) {
      setDirty(true);
    }
  }, [serverJson, dirty, versionId, setDirty]);

  useEffect(() => {
    setMode("form");
    setSectionPath((prev) => prev || "game.puzzleSteps");
  }, [versionId]);

  const treeValue = useMemo(() => {
    if (mode === "raw") {
      const parsed = parseJsonText(rawText);
      return parsed.ok ? parsed.value : null;
    }
    return draft;
  }, [mode, rawText, draft]);

  const updateDraft = (next: unknown) => {
    invalidateInFlightCheck();
    setDraft(next);
    setDirty(true);
    setValidateIssues([]);
    setMediaIssues([]);
    setCheckNote(null);
    setFixNote(null);
    setFixError(null);
  };

  const switchMode = (next: PhoneJsonEditorMode) => {
    if (next === mode) return;
    if (mode === "raw") {
      const parsed = parseJsonText(rawText);
      if (!parsed.ok) {
        setRawError(parsed.error);
        return;
      }
      setDraft(parsed.value);
      setRawError(null);
    } else if (next === "raw") {
      setRawText(draft == null ? "" : JSON.stringify(draft, null, 2));
      setRawError(null);
    }
    setMode(next);
  };

  const commitNormalized = (value: unknown): unknown => {
    const normalized = normalizeEnumFields(value, CASE_TYPE_SCHEMA);
    if (normalized !== value) {
      setDraft(normalized);
      setDirty(true);
      if (mode === "raw") {
        setRawText(JSON.stringify(normalized, null, 2));
      }
    }
    return normalized;
  };

  const readCurrentValue = ():
    | { ok: true; value: unknown }
    | { ok: false; error: string } => {
    if (mode === "raw") {
      const parsed = parseJsonText(rawText);
      if (!parsed.ok) return { ok: false, error: parsed.error };
      return { ok: true, value: parsed.value };
    }
    return { ok: true, value: draft };
  };

  const applyValidatedIssues = (value: unknown) => {
    const result = validateEditorPhoneJson(value, phoneRuntimeVersion);
    setValidateIssues(result.ok ? [] : result.errors);
    return result;
  };

  const onCheck = async () => {
    if (checkBusy || fixBusy || saveBusy) return;
    const current = readCurrentValue();
    if (!current.ok) {
      setRawError(current.error);
      setValidateIssues([]);
      setMediaIssues([]);
      setCheckNote(null);
      return;
    }
    setRawError(null);
    const value = commitNormalized(current.value);
    const schema = applyValidatedIssues(value);
    const gen = ++checkGenRef.current;
    setCheckBusy(true);
    setCheckNote(null);
    try {
      const media = await api.getMediaReadiness(versionId, value);
      if (gen !== checkGenRef.current) return;
      setMediaIssues(media.ok ? [] : media.issues);
      if (schema.ok && media.ok) {
        setCheckNote("结构与媒体检查均通过。");
      } else if (schema.ok && !media.ok) {
        setCheckNote(
          media.placeholderCount > 0
            ? `结构通过。媒体未就绪 ${media.issues.length} 条，仍有 ${media.placeholderCount} 个占位符。发布会被拦住；保存不拦。`
            : `结构通过。媒体未就绪 ${media.issues.length} 条。发布会被拦住；保存不拦。`,
        );
      }
    } catch (err) {
      if (gen !== checkGenRef.current) return;
      setMediaIssues([]);
      setCheckNote(
        err instanceof ApiError
          ? `结构已检查。媒体检查失败：${err.code}`
          : "结构已检查。媒体检查失败。",
      );
    } finally {
      if (gen === checkGenRef.current) setCheckBusy(false);
    }
  };

  const onSaveClick = async () => {
    const current = readCurrentValue();
    if (!current.ok) {
      setRawError(current.error);
      return;
    }
    if (current.value == null || typeof current.value !== "object") return;
    setRawError(null);
    const value = commitNormalized(current.value);
    const result = applyValidatedIssues(value);
    if (!result.ok) return;
    await onSave(value);
  };

  const onAutoFixClick = async () => {
    if (fixBusy || saveBusy) return;
    const current = readCurrentValue();
    if (!current.ok) {
      setRawError(current.error);
      return;
    }
    if (current.value == null || typeof current.value !== "object") return;
    setRawError(null);
    setFixError(null);
    setFixNote(null);
    const value = commitNormalized(current.value);
    const before = applyValidatedIssues(value);
    if (before.ok) {
      setFixNote("当前已通过校验，无需修复。保存仍需手动点击。");
      return;
    }
    setFixBusy(true);
    try {
      const result = await api.autoFixPhoneJson(versionId, {
        phoneJson: value,
      });
      const next =
        result.phoneJson &&
        typeof result.phoneJson === "object" &&
        !Array.isArray(result.phoneJson)
          ? commitNormalized(result.phoneJson)
          : value;
      setDraft(next);
      setRawText(JSON.stringify(next, null, 2));
      if (result.changed) setDirty(true);
      setValidateIssues(result.issues);
      if (result.ok) {
        setFixNote("校验已通过，请手动保存。自动修复不会写入版本。");
      } else if (result.reason === "validate_quota") {
        setFixNote(
          `本轮最多 2 次 validate，仍有 ${result.issues.length} 条问题。可再次点击自动修复。`,
        );
      } else if (result.reason === "iteration_cap") {
        setFixNote(
          `本轮未修完，仍有 ${result.issues.length} 条问题。可再次点击自动修复。`,
        );
      } else if (result.error) {
        setFixError(result.error);
      } else {
        setFixNote(
          `仍有 ${result.issues.length} 条问题。可再次点击自动修复。`,
        );
      }
    } catch (err) {
      setFixError(
        err instanceof ApiError ? err.code : "phone_json_auto_fix_failed",
      );
    } finally {
      setFixBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex flex-col gap-1.5 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="font-heading text-sm font-medium text-foreground">
              Phone JSON
            </h2>
            <p className="text-xs/relaxed text-muted-foreground">
              用表单增删改内容，无需手写 JSON。检查同时跑结构校验与媒体就绪（与发布门禁相同）。结构问题会挡住保存；媒体未就绪仍可保存，但发布会被拦住。自动修复只改编辑器草稿，不会写入版本；保存需手动点击。保存后预览小手机会重新加载。
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setOpen(false)}
          >
            <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} />
            <span className="sr-only">关闭</span>
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{placeholderCount} 个占位符</Badge>
          {mediaReadyOk != null ? (
            <Badge variant={mediaReadyOk ? "success" : "warning"}>
              媒体{mediaReadyOk ? "已就绪" : "未就绪"}
            </Badge>
          ) : null}
          {dirty ? <Badge variant="warning">未保存</Badge> : null}
          <div className="ml-auto flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant={mode === "form" ? "secondary" : "ghost"}
              onClick={() => switchMode("form")}
            >
              表单
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "tree" ? "secondary" : "ghost"}
              onClick={() => switchMode("tree")}
            >
              树形
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "raw" ? "secondary" : "ghost"}
              onClick={() => switchMode("raw")}
            >
              原始
            </Button>
          </div>
        </div>

        {mode === "form" ? (
          <PhoneJsonEditorProvider versionId={versionId} caseId={caseId}>
            <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
              <SectionNav
                root={draft}
                activePath={sectionPath}
                onSelect={setSectionPath}
              />
              <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1">
                {draft == null ? (
                  <p className="text-sm text-muted-foreground">
                    尚无 Phone JSON。
                  </p>
                ) : (
                  <JsonFormTree
                    root={draft}
                    sectionPath={sectionPath}
                    disabled={!canEdit}
                    onChangeRoot={updateDraft}
                  />
                )}
              </div>
            </div>
          </PhoneJsonEditorProvider>
        ) : null}

        {mode === "tree" ? (
          treeValue != null ? (
            <JsonTree value={treeValue} className="min-h-0 flex-1" />
          ) : (
            <Alert variant="destructive">
              <AlertDescription>
                无效 JSON
                {rawError ? `：${rawError}` : ""}
                。切换到「原始」修复。
              </AlertDescription>
            </Alert>
          )
        ) : null}

        {mode === "raw" ? (
          <Textarea
            id="phone-json-edit"
            value={rawText}
            disabled={!canEdit}
            onChange={(e) => {
              invalidateInFlightCheck();
              setRawText(e.target.value);
              setDirty(true);
              setRawError(null);
              setValidateIssues([]);
              setMediaIssues([]);
              setCheckNote(null);
              setFixNote(null);
              setFixError(null);
            }}
            className="min-h-0 flex-1 resize-none overflow-y-auto font-mono text-xs field-sizing-fixed"
          />
        ) : null}

        {rawError ? <FieldError>{rawError}</FieldError> : null}
        {saveError ? <FieldError>{saveError}</FieldError> : null}
        {fixError ? <FieldError>{fixError}</FieldError> : null}
        {fixNote ? (
          <p className="text-xs text-muted-foreground">{fixNote}</p>
        ) : null}
        {checkNote ? (
          <p className="text-xs text-muted-foreground">{checkNote}</p>
        ) : null}
        {validateIssues.length > 0 ? (
          <div className="max-h-40 overflow-y-auto rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
            <p className="mb-1 font-medium text-destructive">
              校验 {validateIssues.length} 条问题，须全部通过后才能保存
            </p>
            <ul className="flex flex-col gap-0.5 text-muted-foreground">
              {validateIssues.map((issue, i) => (
                <li key={`${issue.path}-${i}`}>
                  {formatReadyGateIssueLine({
                    path: issue.path,
                    reason: issue.message,
                  })}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {mediaIssues.length > 0 ? (
          <div className="max-h-40 overflow-y-auto rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
            <p className="mb-1 font-medium text-foreground">
              媒体未就绪 {mediaIssues.length} 条，发布会被拦住
            </p>
            <ul className="flex flex-col gap-0.5 text-muted-foreground">
              {mediaIssues.map((issue, i) => (
                <li key={`${issue.path}-${issue.reason}-${i}`}>
                  {formatReadyGateIssueLine(issue)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-2 border-t border-border p-4">
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={
                checkBusy ||
                fixBusy ||
                saveBusy ||
                (draft == null && !rawText.trim())
              }
              onClick={() => void onCheck()}
            >
              {checkBusy ? <Spinner /> : null}
              {checkBusy ? "检查中…" : "检查"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={
                checkBusy ||
                fixBusy ||
                saveBusy ||
                (mode === "raw" ? !rawText.trim() : draft == null)
              }
              onClick={() => void onAutoFixClick()}
            >
              {fixBusy ? <Spinner /> : null}
              {fixBusy ? "修复中…" : "自动修复"}
            </Button>
            <Button
              disabled={
                saveBusy ||
                fixBusy ||
                checkBusy ||
                !dirty ||
                validateIssues.length > 0 ||
                (mode === "raw" ? !rawText.trim() : draft == null)
              }
              onClick={() => void onSaveClick()}
            >
              {saveBusy ? <Spinner /> : null}
              保存
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {readOnlyReason ?? "此版本为只读。"}
          </p>
        )}
      </div>
    </div>
  );
}
